'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  HoverCard,
  HttpEditor,
  LabelDefault,
  Splitter,
  type HttpEditorAction,
} from '@terra/ui-design-system';
import { Portal } from '@ark-ui/react';
import {
  formatHttpRequestRaw,
  formatHttpResponseRaw,
} from '../utils/formatHttpForPanel';
import {
  normalizeHttpVersionNumber,
  normalizeHttpHeaders,
} from '@terra/shared-utils';
import {
  getDefaultStatusMessage,
  truncateBodyForChat,
} from '../utils/httpFormatter';
import {
  SourceCodeIcon,
  Message01Icon,
  Edit03Icon,
  ReplayIcon,
} from '@terra/ui-icons';
import { useMemo, useCallback } from 'react';
import type { HttpRequest } from '../store/proxy-store';
import {
  createHttpRequestMention,
  createHttpResponseMention,
} from '../utils/httpMentionUtils';
import { useChatStore } from '../store/chatStore';
import { useProxyGrpcBodyView } from '../hooks/useProxyGrpcBodyView';
import {
  resolveProxyRequestDisplayContent,
  resolveProxyResponseDisplayContent,
} from '../utils/proxyGrpcDisplay.utils';
import { OriginalRequestPopoverContent } from './OriginalRequestPopoverContent';

type ProxyRequest = HttpRequest;

export type LazyBodyState = {
  body: string | undefined;
  isLargeBody: boolean;
  isFullyLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  loadFullBody: () => void;
  hasMoreLines?: boolean;
  loadMoreLines?: () => void;
  displayedLineCount?: number;
  totalLineCount?: number;
  loadProgress?: number;
};
export type CachedBodies = {
  requestBody?: string | null;
  responseBody?: string | null;
  originalRequestBody?: string | null;
} | null;
export type GlobalRequestDetailsPanelProps = {
  applicationId?: string;
  selectedRequest: ProxyRequest;
  requestBody: string | undefined;
  responseBody: string | undefined;
  requestLazyBody: LazyBodyState;
  responseLazyBody: LazyBodyState;
  getBodies: (id: string) => CachedBodies;
  onReplay: (request: ProxyRequest) => void;
  openDecoderWith: (text: string) => void;
  appendToInput: (text: string) => void;
};
/**
 * Displays request/response details using HttpEditor components.
 * Used in GlobalRequestsTab for viewing intercepted requests.
 */
export function GlobalRequestDetailsPanel({
  applicationId,
  selectedRequest,
  requestBody,
  responseBody,
  requestLazyBody,
  responseLazyBody,
  getBodies,
  onReplay,
  openDecoderWith,
  appendToInput,
}: GlobalRequestDetailsPanelProps) {
  const { bodyView, grpcHarDisplay, editorProps } = useProxyGrpcBodyView({
    applicationId,
    request: selectedRequest,
    requestBody,
    responseBody,
  });
  const responseToolbarSpacer = (
    <Box w="5" h="5" flexShrink={0} aria-hidden visibility="hidden" />
  );

  const requestContent = useMemo(() => {
    const fallbackContent = formatHttpRequestRaw({
      method: selectedRequest.method,
      url: selectedRequest.url,
      headers: selectedRequest.headers,
      rawHeaders: selectedRequest.rawHeaders,
      body: requestBody,
      httpVersion: normalizeHttpVersionNumber(selectedRequest.httpVersion),
    });

    return resolveProxyRequestDisplayContent({
      grpcHarDisplay,
      bodyView,
      fallbackContent,
    });
  }, [
    selectedRequest.method,
    selectedRequest.url,
    selectedRequest.headers,
    selectedRequest.rawHeaders,
    requestBody,
    selectedRequest.httpVersion,
    grpcHarDisplay,
    bodyView,
  ]);
  const responseContent = useMemo(() => {
    if (!selectedRequest.status) return '';

    const fallbackContent = formatHttpResponseRaw({
      status: selectedRequest.status ?? 0,
      statusMessage:
        selectedRequest.responseStatusMessage ||
        getDefaultStatusMessage(selectedRequest.status ?? 0),
      headers: selectedRequest.responseHeaders || {},
      rawHeaders: selectedRequest.responseRawHeaders,
      body: responseBody,
      httpVersion: normalizeHttpVersionNumber(
        selectedRequest.responseHttpVersion,
      ),
    });

    return resolveProxyResponseDisplayContent({
      grpcHarDisplay,
      bodyView,
      fallbackContent,
    });
  }, [
    selectedRequest.status,
    selectedRequest.responseStatusMessage,
    selectedRequest.responseHeaders,
    selectedRequest.responseRawHeaders,
    responseBody,
    selectedRequest.responseHttpVersion,
    grpcHarDisplay,
    bodyView,
  ]);
  const { sendHttpMentionsToChat } = useChatStore();
  // Normalize headers for mention creation
  const normalizedRequestHeaders = useMemo((): Record<string, string> => {
    return Object.fromEntries(
      Object.entries(normalizeHttpHeaders(selectedRequest.headers)).map(
        ([key, value]) => [
          key,
          Array.isArray(value) ? value.join(', ') : value,
        ],
      ),
    );
  }, [selectedRequest.headers]);
  const normalizedResponseHeaders = useMemo((): Record<string, string> => {
    if (!selectedRequest.responseHeaders) return {};

    return Object.fromEntries(
      Object.entries(normalizeHttpHeaders(selectedRequest.responseHeaders)).map(
        ([key, value]) => [
          key,
          Array.isArray(value) ? value.join(', ') : value,
        ],
      ),
    );
  }, [selectedRequest.responseHeaders]);
  // Handler for sending request to chat with rich display
  const handleSendRequestToChat = useCallback(
    (content: string, selectedText?: string) => {
      if (selectedText) {
        appendToInput(selectedText);

        return;
      }

      const truncatedBody = truncateBodyForChat(requestBody);
      const mention = createHttpRequestMention({
        id: selectedRequest.id,
        method: selectedRequest.method,
        url: selectedRequest.url,
        headers: normalizedRequestHeaders,
        body: truncatedBody,
        httpVersion: normalizeHttpVersionNumber(selectedRequest.httpVersion),
      });

      sendHttpMentionsToChat({
        mentions: [mention],
      });
    },
    [
      selectedRequest,
      normalizedRequestHeaders,
      requestBody,
      appendToInput,
      sendHttpMentionsToChat,
    ],
  );
  // Handler for sending response to chat with rich display
  const handleSendResponseToChat = useCallback(
    (content: string, selectedText?: string) => {
      if (selectedText) {
        appendToInput(selectedText);

        return;
      }

      const truncatedBody = truncateBodyForChat(responseBody);
      const mention = createHttpResponseMention({
        id: selectedRequest.id,
        method: selectedRequest.method,
        url: selectedRequest.url,
        statusCode: selectedRequest.status || 0,
        statusText: selectedRequest.responseStatusMessage,
        headers: normalizedResponseHeaders,
        body: truncatedBody,
        httpVersion: normalizeHttpVersionNumber(
          selectedRequest.responseHttpVersion,
        ),
      });

      sendHttpMentionsToChat({
        mentions: [mention],
      });
    },
    [
      selectedRequest,
      normalizedResponseHeaders,
      responseBody,
      appendToInput,
      sendHttpMentionsToChat,
    ],
  );
  // Custom actions for request editor
  const requestActions: HttpEditorAction[] = useMemo(
    () => [
      {
        id: 'send-to-decoder',
        label: 'Send to Decoder',
        icon: SourceCodeIcon,
        useSelection: true,
        onClick: (content, selectedText) => {
          const text = selectedText || content;

          if (text) {
            openDecoderWith(text);
          }
        },
      },
      {
        id: 'send-to-replay',
        label: 'Send to Replay',
        icon: ReplayIcon,
        onClick: () => {
          onReplay(selectedRequest);
        },
      },
      {
        id: 'send-to-chat',
        label: 'Send to Chat',
        icon: Message01Icon,
        useSelection: true,
        onClick: handleSendRequestToChat,
      },
    ],
    [selectedRequest, openDecoderWith, onReplay, handleSendRequestToChat],
  );
  // Custom actions for response editor
  const responseActions: HttpEditorAction[] = useMemo(
    () => [
      {
        id: 'send-to-decoder',
        label: 'Send to Decoder',
        icon: SourceCodeIcon,
        useSelection: true,
        onClick: (content, selectedText) => {
          const text = selectedText || content;

          if (text) {
            openDecoderWith(text);
          }
        },
      },
      {
        id: 'send-to-replay',
        label: 'Send to Replay',
        icon: ReplayIcon,
        onClick: () => {
          onReplay(selectedRequest);
        },
      },
      {
        id: 'send-to-chat',
        label: 'Send to Chat',
        icon: Message01Icon,
        useSelection: true,
        onClick: handleSendResponseToChat,
      },
    ],
    [selectedRequest, openDecoderWith, onReplay, handleSendResponseToChat],
  );

  return (
    <VStack alignItems="stretch" gap="0" h="full" w="full">
      <Splitter.Root
        defaultSize={[50, 50]}
        panels={[{ id: 'request' }, { id: 'response' }]}
        orientation="horizontal"
        style={{
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Request Panel */}
        <Splitter.Panel id="request" borderWidth="0" w="full" h="full">
          <Box h="full" w="full" display="flex" flexDirection="column" minH={0}>
            <HStack
              px="2"
              py="1"
              bg="bg.subtle"
              borderBottomWidth="1px"
              borderColor="border.muted"
              gap="2"
              justifyContent="space-between"
              flexShrink={0}
            >
              <HStack gap="2">
                <LabelDefault>
                  {selectedRequest.wasEdited
                    ? 'Request (Edited)'
                    : selectedRequest.wasProxyModified
                      ? 'Request (Modified by Proxy)'
                      : 'Request'}
                </LabelDefault>
                {(selectedRequest.wasEdited ||
                  selectedRequest.wasProxyModified) &&
                  selectedRequest.originalRequest && (
                    <HoverCard.Root
                      openDelay={200}
                      closeDelay={200}
                      positioning={{
                        strategy: 'fixed',
                        placement: 'bottom-start',
                        gutter: 8,
                        overflowPadding: 8,
                      }}
                    >
                      <HoverCard.Trigger asChild>
                        <Box as="span" display="inline-flex" cursor="default">
                          <Badge
                            size="sm"
                            variant="outline"
                            colorPalette="orange"
                            leadingContent={<Edit03Icon size={10} />}
                          >
                            {selectedRequest.wasEdited ? 'Edited' : 'Modified'}
                          </Badge>
                        </Box>
                      </HoverCard.Trigger>
                      <Portal>
                        <HoverCard.Positioner>
                          <HoverCard.Content
                            p="0"
                            maxW="400px"
                            overflow="hidden"
                            bg="bg.default"
                            borderRadius="lg"
                            boxShadow="lg"
                            zIndex="popover"
                          >
                            <OriginalRequestPopoverContent
                              method={selectedRequest.originalRequest.method}
                              url={selectedRequest.originalRequest.url}
                              headers={selectedRequest.originalRequest.headers}
                              httpVersion={selectedRequest.httpVersion}
                              originalBody={
                                getBodies(selectedRequest.id)
                                  ?.originalRequestBody ||
                                selectedRequest.originalRequest.body ||
                                undefined
                              }
                            />
                          </HoverCard.Content>
                        </HoverCard.Positioner>
                      </Portal>
                    </HoverCard.Root>
                  )}
              </HStack>
            </HStack>
            <Box flex="1" overflow="hidden">
              <HttpEditor
                value={requestContent}
                canonicalRequestUrl={selectedRequest.url}
                height="100%"
                readOnly
                showViewModeToggle
                {...editorProps}
                customActions={requestActions}
                onSendToReplay={() => onReplay(selectedRequest)}
                lazyLoading={{
                  isLargeBody: requestLazyBody.isLargeBody,
                  isFullyLoaded: requestLazyBody.isFullyLoaded,
                  isLoading: requestLazyBody.isLoading,
                  error: requestLazyBody.error,
                  onLoadFull: requestLazyBody.loadFullBody,
                  hasMoreLines: requestLazyBody.hasMoreLines,
                  onLoadMore: requestLazyBody.loadMoreLines,
                  displayedLineCount: requestLazyBody.displayedLineCount,
                  totalLineCount: requestLazyBody.totalLineCount,
                  loadProgress: requestLazyBody.loadProgress,
                }}
              />
            </Box>
          </Box>
        </Splitter.Panel>

        <Splitter.ResizeTrigger
          id="request:response"
          width="1px"
          minWidth="1px"
          bg="border.subtle"
          _hover={{
            bg: 'border.default',
          }}
        />

        {/* Response Panel */}
        <Splitter.Panel id="response" borderWidth="0" w="full" h="full">
          <Box
            h="full"
            w="full"
            display="flex"
            flexDirection="column"
            minH={0}
            overflow="hidden"
          >
            {selectedRequest.proxyError ? (
              <Box
                p="2"
                flexShrink={0}
                borderBottomWidth="1px"
                borderColor="border.subtle"
              >
                <BodyDefault
                  color="fg.error"
                  lineClamp={3}
                  title={selectedRequest.proxyError}
                >
                  Proxy error: {selectedRequest.proxyError}
                </BodyDefault>
              </Box>
            ) : null}
            {selectedRequest.proxyWarnings &&
            selectedRequest.proxyWarnings.length > 0 ? (
              <Box
                p="2"
                flexShrink={0}
                borderBottomWidth="1px"
                borderColor="border.subtle"
              >
                <BodyDefault color="fg.warning" lineClamp={2}>
                  {selectedRequest.proxyWarnings.join(' ')}
                </BodyDefault>
              </Box>
            ) : null}
            <Box flex="1" minH={0} overflow="hidden">
              {selectedRequest.status ? (
                <HttpEditor
                  value={responseContent}
                  title="Response"
                  statusCode={selectedRequest.status}
                  height="100%"
                  readOnly
                  showViewModeToggle
                  {...editorProps}
                  toolbarContent={responseToolbarSpacer}
                  customActions={responseActions}
                  lazyLoading={{
                    isLargeBody: responseLazyBody.isLargeBody,
                    isFullyLoaded: responseLazyBody.isFullyLoaded,
                    isLoading: responseLazyBody.isLoading,
                    error: responseLazyBody.error,
                    onLoadFull: responseLazyBody.loadFullBody,
                    hasMoreLines: responseLazyBody.hasMoreLines,
                    onLoadMore: responseLazyBody.loadMoreLines,
                    displayedLineCount: responseLazyBody.displayedLineCount,
                    totalLineCount: responseLazyBody.totalLineCount,
                    loadProgress: responseLazyBody.loadProgress,
                  }}
                />
              ) : null}
            </Box>
          </Box>
        </Splitter.Panel>
      </Splitter.Root>
    </VStack>
  );
}
